import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';

export interface Strokes {
  w: number;
  h: number;
  strokes: [number, number][][];
}
export interface SignaturePadHandle {
  clear(): void;
  value(): Strokes | null;
}

/**
 * Signing with a finger, a pen or the mouse. Keeps the lines as points (not a picture), so the
 * signature can be drawn sharp at any size: on the screen and in the signing certificate.
 */
export function SignaturePad({ label, onChange, ref }: { label: string; onChange: (points: number) => void; ref: Ref<SignaturePadHandle> }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<[number, number][][]>([]);
  const drawing = useRef(false);
  const size = useRef({ w: 0, h: 0 });

  const ink = () => getComputedStyle(canvas.current!).getPropertyValue('--paper-ink').trim() || 'black';
  const redraw = () => {
    const c = canvas.current!;
    const g = c.getContext('2d')!;
    g.clearRect(0, 0, c.width, c.height);
    g.lineWidth = 2.4;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.strokeStyle = ink();
    for (const s of strokes.current) {
      g.beginPath();
      s.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
      if (s.length === 1) g.lineTo(s[0]![0] + 0.1, s[0]![1]);
      g.stroke();
    }
  };
  useEffect(() => {
    const c = canvas.current!;
    const fit = () => {
      const ratio = window.devicePixelRatio || 1;
      const { width, height } = c.getBoundingClientRect();
      // Points are kept in the pad's own size: a new size starts a clean pad.
      if (size.current.w && (Math.abs(size.current.w - width) > 1 || Math.abs(size.current.h - height) > 1)) {
        strokes.current = [];
        onChange(0);
      }
      size.current = { w: width, h: height };
      c.width = width * ratio;
      c.height = height * ratio;
      c.getContext('2d')!.setTransform(ratio, 0, 0, ratio, 0, 0);
      redraw();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    clear() {
      strokes.current = [];
      redraw();
      onChange(0);
    },
    value() {
      const n = strokes.current.reduce((s, x) => s + x.length, 0);
      return n ? { w: Math.round(size.current.w), h: Math.round(size.current.h), strokes: strokes.current } : null;
    },
  }));

  const point = (e: React.PointerEvent): [number, number] => {
    const r = canvas.current!.getBoundingClientRect();
    return [Math.round((e.clientX - r.left) * 10) / 10, Math.round((e.clientY - r.top) * 10) / 10];
  };
  const down = (e: React.PointerEvent) => {
    canvas.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    strokes.current.push([point(e)]);
    redraw();
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const s = strokes.current[strokes.current.length - 1]!;
    const p = point(e);
    const last = s[s.length - 1]!;
    if (Math.hypot(p[0] - last[0], p[1] - last[1]) < 1.5) return;
    s.push(p);
    const g = canvas.current!.getContext('2d')!;
    g.lineWidth = 2.4;
    g.lineCap = 'round';
    g.strokeStyle = ink();
    g.beginPath();
    g.moveTo(last[0], last[1]);
    g.lineTo(p[0], p[1]);
    g.stroke();
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(strokes.current.reduce((s, x) => s + x.length, 0));
  };
  return <canvas ref={canvas} className="fs-pad sign-pad" aria-label={label} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />;
}

/** A signature saved as points, drawn as a picture. */
export function SignatureView({ value, label }: { value: Strokes; label: string }) {
  const d = value.strokes.map((s) => s.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ') + (s.length === 1 ? ` l0.1 0` : '')).join(' ');
  return (
    <svg className="sign-view" viewBox={`0 0 ${value.w} ${value.h}`} role="img" aria-label={label} preserveAspectRatio="xMidYMid meet">
      <path d={d} fill="none" stroke="currentColor" strokeWidth={Math.max(2, value.w / 180)} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
